import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { LabelsService } from 'src/app/util/services/labels/labels.service';
import { LabelsInterface } from 'src/app/util/services/labels/data/label.inteface';
import { MyAssetConfigurationGeneralService } from './data/configuration-general.service';
import { MyAssetService } from '../data/my-asset.service';
import { InconsistenciesService } from '../inconsistencies/service/inconsistencies.service';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { Subscription, forkJoin } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MspButtonComponent } from 'src/app/page_component_item/msp-button/msp-button.component';
import { ToggleSwitchModule } from 'src/app/page_component_item/toggle-switch/toggle-switch.module';
import { DotBounceLoadingEffectComponent } from 'src/app/page_component_item/dot-bounce-loading-effect/dot-bounce-loading-effect.component';

@Component({
	selector: 'app-my-asset-configuration-general',
	standalone: true,
	imports: [CommonModule, FormsModule, MspButtonComponent, ToggleSwitchModule, DotBounceLoadingEffectComponent],
	templateUrl: './configuration-general.component.html',
	styleUrl: './configuration-general.component.scss',
})
export class MyAssetConfigurationGeneralComponent implements OnInit, OnDestroy {
	private readonly labelsService = inject(LabelsService);
	private readonly myAssetConfigService = inject(MyAssetConfigurationGeneralService);
	private readonly myAssetService = inject(MyAssetService);
	private readonly inconsistenciesService = inject(InconsistenciesService);
	private readonly notificationService = inject(NotificationService);

	labels: LabelsInterface = {};
	private subscriptions = new Subscription();

	settings = {
		myAssetEnabled: false,
		enabledInconsistencies: [] as number[],
		inconsistencyPriorities: {} as { [key: number]: string },
		enabledCiTypes: [] as string[],
		enabledCiSubtypes: [] as string[],
	};

	availableInconsistenciesTypes: any[] = [];
	availableOnlyCiTypes: any[] = [];
	availableCiSubtypes: any[] = [];

	isSaving = false;
	dataLoaded = false;

	ngOnInit(): void {
		const labelSub = this.labelsService.getLabels().subscribe((labels) => {
			this.labels = labels;
		});
		this.subscriptions.add(labelSub);

		this.loadData();
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	loadData(): void {
		const inconsistenciesSub = this.inconsistenciesService.getInconsistenciesTypes();
		const ciTypesSub = this.myAssetService.getAvailablesCiTypes();
		const settingsSub = this.myAssetConfigService.getSettings();

		const forkSub = forkJoin({
			inconsistencies: inconsistenciesSub,
			ciTypes: ciTypesSub,
			settings: settingsSub,
		}).subscribe({
			next: (res) => {
				this.availableInconsistenciesTypes =
					res.settings && res.settings.availableInconsistenciesTypes
						? res.settings.availableInconsistenciesTypes
						: res.inconsistencies || [];

				const ciTypesList = res.ciTypes || [];
				this.availableOnlyCiTypes = ciTypesList.filter((ciType: any) => ciType.subType === '' || !ciType.subType);
				this.availableCiSubtypes = ciTypesList.filter((ciType: any) => ciType.subType !== '' && ciType.subType);

				if (res.settings) {
					this.settings = {
						myAssetEnabled: !!res.settings.myAssetEnabled,
						enabledInconsistencies: res.settings.enabledInconsistencies || [],
						inconsistencyPriorities: res.settings.inconsistencyPriorities || {},
						enabledCiTypes: res.settings.enabledCiTypes || [],
						enabledCiSubtypes: res.settings.enabledCiSubtypes || [],
					};
				}
				this.dataLoaded = true;
			},
			error: (error) => {
				console.error('Erro ao carregar dados de configuração do MyAsset:', error);
				this.notificationService.setNotificationMessage(this.labels.lbl_erro_0113 || 'Erro ao carregar as configurações.');
				this.dataLoaded = true;
			},
		});
		this.subscriptions.add(forkSub);
	}

	isInconsistencyEnabled(id: number): boolean {
		return this.settings.enabledInconsistencies.includes(id);
	}

	toggleInconsistency(id: number): void {
		if (this.isInconsistencyEnabled(id)) {
			this.settings.enabledInconsistencies = this.settings.enabledInconsistencies.filter((item) => item !== id);
			delete this.settings.inconsistencyPriorities[id];
		} else {
			this.settings.enabledInconsistencies.push(id);
			this.settings.inconsistencyPriorities[id] = 'Média';
		}
	}

	isCiTypeEnabled(id: any): boolean {
		return this.settings.enabledCiTypes.some((item) => String(item) === String(id));
	}

	toggleCiType(id: any): void {
		const targetId = typeof id === 'number' ? id : Number.parseInt(id, 10) || id;
		if (this.isCiTypeEnabled(targetId)) {
			this.settings.enabledCiTypes = this.settings.enabledCiTypes.filter((item) => String(item) !== String(targetId));
		} else {
			this.settings.enabledCiTypes.push(targetId);
		}
	}

	isCiSubtypeEnabled(subId: any): boolean {
		return this.settings.enabledCiSubtypes.some((item) => String(item) === String(subId));
	}

	toggleCiSubtype(subId: any): void {
		const targetSubId = typeof subId === 'number' ? subId : Number.parseInt(subId, 10) || subId;
		if (this.isCiSubtypeEnabled(targetSubId)) {
			this.settings.enabledCiSubtypes = this.settings.enabledCiSubtypes.filter((item) => String(item) !== String(targetSubId));
		} else {
			this.settings.enabledCiSubtypes.push(targetSubId);
		}
	}

	getParentTypeName(parentId: any): string {
		const parent = this.availableOnlyCiTypes.find((p) => String(p.ciType) === String(parentId));
		return parent ? parent.type || '' : '';
	}

	saveSettings(): void {
		if (this.isSaving) return;
		this.isSaving = true;

		this.myAssetConfigService.saveSettings(this.settings).subscribe({
			next: () => {
				this.notificationService.setNotificationMessage(
					this.labels.lbl_successful_updated || 'Configurações atualizadas com sucesso!'
				);
				this.isSaving = false;
			},
			error: () => {
				this.notificationService.setNotificationMessage(this.labels.lbl_erro_0113 || 'Erro ao salvar as configurações.');
				this.isSaving = false;
			},
		});
	}
}
